<template>
  <div class="reject-reason-modal" dir="rtl">
    <p class="intro">{{ intro }}</p>

    <div class="ctx" v-if="candidateName || positionTitle">
      <span v-if="candidateName" class="ctx-item">{{ candidateName }}</span>
      <span v-if="candidateName && positionTitle" class="ctx-sep">·</span>
      <span v-if="positionTitle" class="ctx-item">{{ positionTitle }}</span>
    </div>

    <label class="field-label">סיבת הפסילה</label>
    <div class="chips" role="radiogroup" aria-label="סיבת הפסילה">
      <button
        v-for="cat in categories"
        :key="cat.key"
        type="button"
        class="chip"
        :class="{ selected: selectedCategory === cat.key }"
        :title="cat.hint || ''"
        role="radio"
        :aria-checked="selectedCategory === cat.key ? 'true' : 'false'"
        :disabled="is_submitting"
        @click="selectCategory(cat.key)"
      >{{ cat.label }}</button>
    </div>

    <label class="field-label" for="reject-reason-text">הסבר קצר</label>
    <textarea
      id="reject-reason-text"
      class="reason-textarea"
      dir="rtl"
      rows="3"
      maxlength="500"
      :disabled="is_submitting"
      v-model="reasonText"
      placeholder="מה ידעת שהסוכן לא ידע?"
    ></textarea>
    <div class="counter-row">
      <span class="counter" :class="{ over: reasonText.length > 500 }">{{ reasonText.length }}/500</span>
      <span class="hint">משפט אחד קונקרטי מספיק.</span>
    </div>

    <div class="actions">
      <button
        type="button"
        class="btn btn-danger"
        :disabled="!canSubmit || is_submitting"
        @click="submit"
      >{{ is_submitting ? '...פוסל' : 'פסילה' }}</button>
      <button
        type="button"
        class="btn btn-plain"
        :disabled="is_submitting"
        @click="close"
      >ביטול</button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { bus } from '@/custom/classes/bus.js'
import { REJECTION_CATEGORIES } from '@/custom/classes/rejection_reasons.js'

export default {
  name: 'ChatbotRejectReasonModal',
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      categories: REJECTION_CATEGORIES,
      selectedCategory: '',
      reasonText: '',
      is_submitting: false
    }
  },
  computed: {
    row () {
      return (this.payload && this.payload.data) || {}
    },
    candidateName () {
      return this.row.applicant_name || (this.row.applicant && this.row.applicant.name) || ''
    },
    positionTitle () {
      return this.row.position_title || (this.row.position && this.row.position.title) || ''
    },
    // Intro copy depends on whether this reject is a DISAGREEMENT with BA. When BA
    // judged the candidate a fit (submit/green) the reason feeds learning, so we say
    // so. Otherwise (BA rejected / needs_info / no decision yet) the reason is simply
    // recorded and displayed. Feminine address (בחרי/כתבי) is the product convention.
    intro () {
      const bd = this.row.ba_decision
      if (bd === 'submit' || bd === 'green') {
        return 'המערכת סברה שהמועמד/ת מתאים/ה למשרה. כדי לפסול, בחרי קטגוריה וכתבי משפט אחד קצר — זה מה שעוזר למערכת ללמוד.'
      }
      return 'כדי לפסול, בחרי קטגוריה וכתבי משפט אחד קצר — סיבת הפסילה תישמר ותוצג בעמוד ההצעות.'
    },
    // Zero-friction honest input: a chosen category and a non-empty, short reason.
    // Minimum is intentionally low (3 chars) — we want a concrete sentence, not an essay.
    canSubmit () {
      const text = this.reasonText.trim()
      return !!this.selectedCategory && text.length >= 3 && text.length <= 500
    }
  },
  methods: {
    ...mapMutations('modals', ['remove']),
    selectCategory (key) {
      if (this.is_submitting) return
      this.selectedCategory = key
    },
    submit () {
      if (this.is_submitting || !this.canSubmit) return
      const id = this.row.id
      if (!id) {
        this.$utils.emit('show_toast', { text: 'לא נמצאה הצעה לפסילה', type: 'error' })
        return
      }
      this.is_submitting = true
      this.$network_utils
        .rejectChatbotProposal({
          id: id,
          rejection_reason_category: this.selectedCategory,
          rejection_reason_text: this.reasonText.trim()
        })
        .then(response => {
          // The rejection + reason persist regardless; learning_relay tells us
          // whether the feedback reached the rule-drafting pipeline. Surface a
          // relay failure so the recruiter knows the learning half didn't land.
          if (response && response.learning_relay === 'failed') {
            this.$utils.emit('show_toast', {
              text: 'הדחייה נשמרה, אך שליחת הפידבק ללמידה נכשלה — נסי שוב מהכרטיס',
              type: 'warning'
            })
          } else {
            this.$utils.emit('show_toast', { text: 'ההצעה נפסלה והסיבה נשמרה' })
          }
          bus.$emit('chatbot_proposal_handled')
          this.close()
        })
        .catch(error => {
          const serverError = error && error.response && error.response.data && error.response.data.error
          this.$utils.emit('show_toast', {
            text: serverError || 'שגיאה בפסילת ההצעה — נסי שוב',
            type: 'error'
          })
        })
        .finally(() => {
          this.is_submitting = false
        })
    },
    close () {
      this.remove(this.payload)
    }
  }
}
</script>

<style scoped>
.reject-reason-modal {
  padding: 8px 4px;
  text-align: right;
  min-width: 460px;
  max-width: 560px;
}
.intro {
  margin: 0 0 12px 0;
  font-size: 13px;
  line-height: 1.55;
  color: #4b5563;
  background: #f5f6fe;
  border: 1px solid #dbe0f5;
  border-radius: 8px;
  padding: 8px 10px;
}
.ctx {
  font-size: 13px;
  color: #2c3e50;
  margin-bottom: 12px;
}
.ctx-item {
  font-weight: 700;
}
.ctx-sep {
  margin: 0 6px;
  color: #9aa5b1;
}
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  margin: 4px 0 8px 0;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.chip {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 20px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #34495e;
  cursor: pointer;
  transition: all 0.15s ease;
}
.chip:hover:not(:disabled) {
  border-color: #3273dc;
  background: #f4f8ff;
}
.chip.selected {
  background: #3273dc;
  border-color: #3273dc;
  color: #fff;
}
.chip:disabled {
  opacity: 0.55;
  cursor: default;
}
.reason-textarea {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  padding: 10px;
  font-size: 14px;
  line-height: 1.4;
  resize: vertical;
}
.counter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.counter {
  font-size: 12px;
  color: #7f8c8d;
}
.counter.over {
  color: #c0392b;
  font-weight: 700;
}
.hint {
  font-size: 12px;
  color: #6b7280;
}
.actions {
  display: flex;
  gap: 12px;
  margin-top: 18px;
}
.btn {
  border-radius: 8px;
  padding: 9px 22px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
}
.btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.btn-danger {
  background: #e74c3c;
  border-color: #e74c3c;
  color: #fff;
}
.btn-danger:hover:not(:disabled) {
  background: #cf3d2e;
  border-color: #cf3d2e;
}
.btn-plain {
  background: #fff;
  border-color: #cbd5e0;
  color: #475569;
}
.btn-plain:hover:not(:disabled) {
  background: #f1f5f9;
}
</style>
